// Pinia store sesi pengguna internal (JWT akses + refresh — TEK §5.1).
// Peserta/publik tidak memakai store ini (mereka pakai token magic-link
// tersendiri, lihat src/pages/publik).
//
// Refresh token TIDAK lagi disimpan di sini: ia hidup di cookie HttpOnly yang
// dipasang server pada jalur /api/v1/auth (gelombang 7, review kode K3) —
// tak terbaca skrip, jadi tak bisa dicuri lewat XSS. Yang disimpan di
// localStorage hanya token akses (15 menit) & profil pengguna. Sesi lama yang
// masih menyimpan refresh token di localStorage dimigrasikan sekali ke cookie
// lalu dihapus (migrasiSesiLama).
import { defineStore } from 'pinia'
import { ApiError } from '@/lib/api'

export type RoleName =
  | 'REDAKTUR'
  | 'KORPS_MADYA'
  | 'BIDANG_KADER'
  | 'PIMP_CABANG'
  | 'PERANGKAT'
  | 'SEKRETARIAT'
  | 'PENGURUS_BIDANG'
  | 'PENDAMPING'
  | 'SUPER_ADMIN'

export interface AuthUser {
  id: string
  nama: string
  email: string
  roles: RoleName[]
  two_factor: boolean
  must_change_password: boolean
}

interface AuthResponse {
  access_token: string
  user: AuthUser
}

// Satu permintaan refresh pada satu waktu per tab: beberapa panggilan API
// yang serentak menerima 401 menunggu hasil refresh yang sama.
let refreshBerjalan: Promise<boolean> | null = null

const STORAGE_KEY = 'sim-perkaderan.auth'

export const useAuthStore = defineStore('auth', {
  state: () => {
    const persisted = loadPersisted()
    return {
      accessToken: persisted?.accessToken ?? null as string | null,
      // Hanya untuk migrasi sesi lama (refresh token warisan localStorage).
      refreshLama: persisted?.refreshToken ?? null as string | null,
      user: persisted?.user ?? null as AuthUser | null,
    }
  },
  getters: {
    isAuthenticated: (state) => !!state.accessToken,
    hasRole: (state) => (...roles: RoleName[]) => !!state.user && roles.some((r) => state.user!.roles.includes(r)),
  },
  actions: {
    // totpCode kosong pada akun ber-2FA membalas code='2FA_REQUIRED' (bukan
    // gagal total) — pemanggil (Login.vue) menampilkan langkah kedua lalu
    // memanggil ulang login() dengan kode terisi, tanpa mengulang password.
    async login(email: string, password: string, totpCode?: string) {
      const res = await fetch('/api/v1/auth/login', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, totp_code: totpCode ?? '' }),
      })
      const data = await res.json()
      if (!res.ok) {
        throw new ApiError(res.status, data.code ?? 'UNKNOWN', data.message ?? 'Login gagal')
      }
      this.setSession(data as AuthResponse)
    },
    // Refresh memakai cookie HttpOnly; refresh token warisan (bila masih ada)
    // ikut dikirim sekali agar server memindahkannya ke cookie.
    tryRefresh(): Promise<boolean> {
      if (refreshBerjalan) return refreshBerjalan
      refreshBerjalan = (async () => {
        try {
          const res = await fetch('/api/v1/auth/refresh', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(this.refreshLama ? { refresh_token: this.refreshLama } : {}),
          })
          if (!res.ok) return false
          this.setSession((await res.json()) as AuthResponse)
          return true
        } catch {
          return false
        } finally {
          refreshBerjalan = null
        }
      })()
      return refreshBerjalan
    },
    // Dipanggil sekali saat aplikasi dimuat: sesi lama dipindah ke cookie
    // sekarang juga, supaya refresh token tidak berlama-lama di localStorage.
    async migrasiSesiLama() {
      if (this.refreshLama) await this.tryRefresh()
    },
    setSession(res: AuthResponse) {
      this.accessToken = res.access_token
      this.refreshLama = null
      this.user = res.user
      persist(this.$state)
    },
    // Dipanggil setelah user berhasil mengganti password sendiri — banner
    // "wajib ganti password" hilang tanpa perlu login ulang.
    setEmail(email: string) {
      if (this.user) {
        this.user.email = email
        persist(this.$state)
      }
    },
    setTwoFactor(aktif: boolean) {
      if (this.user) {
        this.user.two_factor = aktif
        persist(this.$state)
      }
    },
    markPasswordChanged() {
      if (this.user) {
        this.user.must_change_password = false
        persist(this.$state)
      }
    },
    async logout() {
      const refreshLama = this.refreshLama
      this.accessToken = null
      this.refreshLama = null
      this.user = null
      localStorage.removeItem(STORAGE_KEY)
      try {
        // Server mencabut token di cookie (atau warisan) & menghapus cookie-nya.
        await fetch('/api/v1/auth/logout', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(refreshLama ? { refresh_token: refreshLama } : {}),
        })
      } catch {
        // Best-effort: sesi lokal sudah dihapus meski request pencabutan gagal.
      }
    },
  },
})

function loadPersisted(): { accessToken: string; refreshToken?: string; user: AuthUser } | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

// Refresh token sengaja tidak ikut disimpan.
function persist(state: { accessToken: string | null; user: AuthUser | null }) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify({ accessToken: state.accessToken, user: state.user }))
}

// Sinkron antartab (uji produksi SYS-04). Masuk/keluar di satu tab mengganti
// cookie refresh untuk SEMUA tab, sementara tab lain masih memegang profil &
// cache data akun sebelumnya sampai dimuat ulang — penguji yang berganti
// peran di tab berbeda melihat akun lama. Event `storage` hanya diterima tab
// LAIN: bila akunnya berganti (atau keluar), muat ulang supaya profil, menu,
// dan cache kueri ikut akun yang sekarang; bila akunnya sama (token
// diperbarui), cukup salin tokennya.
export function pasangSinkronAntarTab(store: ReturnType<typeof useAuthStore>) {
  window.addEventListener('storage', (e) => {
    if (e.key !== STORAGE_KEY && e.key !== null) return // null = storage dikosongkan
    const terbaru = loadPersisted()
    if ((terbaru?.user?.id ?? null) !== (store.user?.id ?? null)) {
      window.location.reload()
      return
    }
    if (terbaru) {
      store.accessToken = terbaru.accessToken
      store.user = terbaru.user
    }
  })
}
